<template>
  <hr-form-dialog
    ref="dialog"
    :title="title"
    @hide="onDialogHide"
    @submit="onSubmitForm"
  >
    <div class="row q-col-gutter-md">
      <p
        v-if="!!caption"
        class="col-12 hr-body-medium q-ma-none"
      >
        {{ caption }}
      </p>
      <hr-tree
        v-model:selected="selected"
        v-model:expanded="expandedItems"
        :nodes="folderTreeItems"
        node-key="id"
        label-key="folderName"
        :aria-label="title"
        class="col-12"
      />
      <div
        v-if="allowNewFolder"
        class="col-12"
      >
        <hr-subtle-btn
          :label="selected === 'new' ? $t('folderDialog.renameFolder') : $t('folderDialog.newFolder')"
          icon="create_new_folder"
          @click="onEditNewFolder"
        />
      </div>
      <hr-text-field
        v-if="showItemNameInput"
        v-model="itemName"
        :label="itemNameLabel"
        :rules="validateItemName"
        autofocus
        hide-bottom-space
        class="col-12"
      />
    </div>

    <template #actions>
      <hr-dismiss-btn
        :label="$t('common.buttonCancel')"
        @click="hide"
      />
      <universal-tooltip
        :show-tooltip="isSubmitDisabled"
        :text="$t('folderDialog.disabledSubmitButtonTooltip')"
      >
        <hr-confirm-btn
          type="submit"
          :label="submitButtonLabel || $t('common.buttonSubmit')"
          :disable="isSubmitDisabled"
        />
      </universal-tooltip>
    </template>
  </hr-form-dialog>
</template>

<script>
import UniversalTooltip from 'components/UI/UniversalTooltip';
import {
  HrConfirmBtn,
  HrDismissBtn,
  HrFormDialog,
  HrSubtleBtn,
  HrTextField,
  HrTree,
  promptDialog,
} from 'src/design-system';

const normalizeFolderName = (name) => (name ?? '').trim().toLowerCase();

export default {
  name: 'FolderDialog',

  components: {
    HrConfirmBtn,
    HrDismissBtn,
    HrFormDialog,
    HrSubtleBtn,
    HrTextField,
    HrTree,
    UniversalTooltip,
  },

  props: {

    title: {
      type: String,
      required: true,
    },

    caption: {
      type: String,
      default: null,
    },

    errorMessages: {
      type: Object,
      default: () => ({}),
    },

    submitButtonLabel: {
      type: String,
      default: null,
    },

    rootName: {
      type: String,
      default: null,
    },

    showItemNameInput: {
      type: Boolean,
      default: false,
    },

    itemNameLabel: {
      type: String,
      default: '',
    },

    preventItemFolderNamesDuplicates: {
      type: Boolean,
      default: false,
    },

    folders: {
      type: Array,
      required: true,
    },

    selectedFolder: {
      type: String,
      default: null,
    },

    allowNewFolder: {
      type: Boolean,
      default: false,
    },
    defaultNewFolderName: {
      type: String,
      default: '',
    },

    disableRootFolder: {
      type: Boolean,
      default: false,
    },
    movingFolderName: {
      type: String,
      default: null,
    },

  },

  emits: ['ok', 'hide'],

  data() {
    return {
      itemName: null,

      newFolderName: '',
      newParentFolder: null,

      selected: null,
      expandedItems: [null],
    };
  },

  computed: {

    isMovingFolderValid() {
      return this.movingFolderName ? this.isFolderNameValid(this.movingFolderName) : true;
    }, // isFolderToMoveValid()

    isNewFolderValid() {
      return this.allowNewFolder && this.newFolderName ? this.isFolderNameValid(this.newFolderName) : true;
    }, // isNewFolderValid()

    isRootSelected() {
      return this.disableRootFolder && this.selected === null;
    }, // isRootSelected()

    isSubmitDisabled() {
      return this.isRootSelected || !this.isMovingFolderValid || !this.isNewFolderValid;
    }, // isSubmitDisabled()

    foldersWithNewFolder() {
      if (this.allowNewFolder && this.newFolderName) {
        return this.folders.concat(
          {
            folderName: this.newFolderName,
            id: 'new',
            parentFolder: this.newParentFolder || null,
            icon: 'create_new_folder',
            header: 'newFolder',
          },
        );
      }
      return this.folders;
    },

    folderTreeItems() {
      return [{
        folderName: this.rootName,
        children: [
          ...this.getFolderChildren(this.foldersWithNewFolder),
        ],
        id: null,
        parentFolder: null,
      }];
    }, // folderTreeItems()

    currentLevelFolderNames() {
      const selectedFolder = this.selected === 'new' ? this.newParentFolder : this.selected;
      return this.folders
        .filter((folder) => folder.parentFolder === selectedFolder)
        .map((folder) => normalizeFolderName(folder.folderName));
    }, // currentLevelFolderNames()

    validateNewFolderName() {
      return [
        (val) => (!!normalizeFolderName(val) || this.errorMessages?.newFolderNameMissing),
        (val) => (
          !this.currentLevelFolderNames.includes(normalizeFolderName(val))
          || this.errorMessages?.newFolderNameDuplicate),
      ];
    }, // validateNewFolderName()

    validateItemName() {
      const rules = [(val) => (!!normalizeFolderName(val) || this.errorMessages?.newFolderNameMissing)];
      if (this.preventItemFolderNamesDuplicates) {
        rules.push(
          (val) => (
            !this.currentLevelFolderNames.includes(normalizeFolderName(val))
          || this.errorMessages?.newFolderNameDuplicate),
        );
      }
      return rules;
    }, // validateItemName()

  },

  methods: {

    show() {
      this.$refs.dialog.show();

      this.newFolderName = this.allowNewFolder ? this.defaultNewFolderName : null;
      this.selected = this.allowNewFolder && this.newFolderName
        ? 'new'
        : this.selectedFolder || null;
      this.expandedItems = this.getExpandedItems(this.selected, [null]);
    },

    hide() {
      if (this.$refs.dialog) {
        this.$refs.dialog.hide();
      }
    },

    onDialogHide() {
      this.$emit('hide');
    },

    async onSubmitForm() {
      this.$emit('ok', {
        parentFolderId: this.selected === 'new' ? this.newParentFolder : this.selected,
        folderName: this.selected === 'new' ? this.newFolderName : null,
        itemName: this.itemName,
      });
      this.hide();
    }, // onSubmitForm()

    onEditNewFolder() {
      const isNewFolderSelected = this.selected === 'new';
      promptDialog(
        isNewFolderSelected ? this.$t('folderDialog.renameFolder') : this.$t('folderDialog.newFolder'),
        this.$t('folderDialog.textFolderNamePrompt'),
        (folderName) => {
          this.newFolderName = folderName;
          if (!isNewFolderSelected) {
            // A new folder goes inside whichever folder was selected when it was made.
            this.newParentFolder = this.selected || null;
          }
          this.selected = 'new';
          this.expandedItems = this.getExpandedItems(this.selected, [null]);
        },
        {
          model: this.newParentFolder === this.selected || isNewFolderSelected ? this.newFolderName : '',
          label: this.$t('common.folderName'),
          rules: this.validateNewFolderName,
          isValid: this.isFolderNameValid,
          confirmLabel: isNewFolderSelected ? undefined : this.$t('folderDialog.buttonCreate'),
        },
      );
    }, // onEditNewFolder()

    getFolderChildren(folders, parentFolder = null) {
      return folders
        .filter((folder) => folder.parentFolder === parentFolder)
        .map((folder) => ({
          ...folder,
          children: this.getFolderChildren(folders, folder.id),
          icon: folder.icon || 'folder',
        }))
        .sort((a, b) => {
          const nameA = a.folderName?.toLowerCase();
          const nameB = b.folderName?.toLowerCase();
          if (nameA === nameB) return 0;
          if (nameA === undefined) return 1;
          if (nameB === undefined) return -1;
          return nameA < nameB ? -1 : 1;
        });
    }, // getFolderChildren()

    getExpandedItems(folderId, expandedItems = []) {
      const folder = this.foldersWithNewFolder.find(({ id }) => id === folderId);
      if (!folder || !folder?.parentFolder) {
        return expandedItems;
      }

      return this.getExpandedItems(folder.parentFolder, [...expandedItems, folder.parentFolder]);
    }, // getExpandedItems()

    isFolderNameValid(folderName) {
      return this.validateNewFolderName.every((rule) => rule(folderName) === true);
    },

  },
};
</script>
